import FormattedMessage from 'I18n/components/FormattedMessage';
import { useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';
import { CollectionProcess, SubscriptionActions, SubscriptionStatus } from '../types/subscriptions';
import { SCOPES } from '../utils/constants';
import { getIsNextPaymentDateToday } from '../utils/dateTime';
import { getIsFirstPaymentProcessing, isComplete, isSubcriptionEditableAndActive, isSubscriptionEditable, isSubscriptionPaymentProcessing, makeActionsRecord } from '../utils/subscriptionUtils';
import { useHasBillingWizardGate } from './useHasGate';
import { useHasScope } from './useHasScope';
import { jsx as _jsx } from "react/jsx-runtime";
const DEFAULT_VALIDATION = makeActionsRecord(() => null);
const loadingMessage = /*#__PURE__*/_jsx(FormattedMessage, {
  message: "subscriptions.Actions.loading"
});
const STILL_LOADING = makeActionsRecord(() => loadingMessage);
export function useActionValidation() {
  const {
    userPermissions,
    paymentsEligibility
  } = useAuth();
  const isJitaUser = useHasScope(SCOPES.JITA_USER);
  const hasWriteAccess = useHasScope(SCOPES.SUBSCRIPTIONS_WRITE);
  const subscription = useSubscription();
  const hasBillingWizardGate = useHasBillingWizardGate();
  const isAutomaticPayments = (subscription === null || subscription === void 0 ? void 0 : subscription.collectionProcess) === CollectionProcess.AutomaticPayments;
  return useMemo(() => {
    if (!subscription) {
      return STILL_LOADING;
    }
    return makeActionsRecord(actionName => {
      if (isJitaUser) {
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: `subscriptions.Actions.${actionName}.errors.jitaUser`
        });
      }
      if (!userPermissions.currentUserCanEdit || !paymentsEligibility.chargesEnabled && !hasBillingWizardGate || !hasWriteAccess && hasBillingWizardGate) {
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: `subscriptions.Actions.${actionName}.errors.unauthorized`
        });
      }
      if (!isSubscriptionEditable(subscription.status)) {
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: `subscriptions.Actions.${actionName}.errors.inactiveSubscription`
        });
      }
      if (isComplete(subscription)) {
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: `subscriptions.Actions.${actionName}.errors.completeSubscription`
        });
      }
      switch (actionName) {
        case SubscriptionActions.UpdatePaymentMethod:
          {
            if (!subscription.buyer) {
              return /*#__PURE__*/_jsx(FormattedMessage, {
                message: "subscriptions.Actions.update-payment-method.errors.missingContactAssociation"
              });
            }
            if (hasBillingWizardGate && !isAutomaticPayments) {
              return /*#__PURE__*/_jsx(FormattedMessage, {
                message: "subscriptions.Actions.update-payment-method.errors.manualPayment"
              });
            }
            break;
          }
        case SubscriptionActions.Edit:
          {
            if (subscription.status === SubscriptionStatus.Paused) {
              return /*#__PURE__*/_jsx(FormattedMessage, {
                message: "subscriptions.Actions.edit.errors.paused"
              });
            }
            if (!isSubcriptionEditableAndActive(subscription.status)) {
              return /*#__PURE__*/_jsx(FormattedMessage, {
                message: "subscriptions.Actions.edit.errors.inactiveSubscription"
              });
            }

            // we allow editing of the subscription even if payment is processing
            if (isSubscriptionPaymentProcessing(subscription.paymentStatus)) {
              return null;
            }
            break;
          }
        case SubscriptionActions.EditNextPaymentDate:
        case SubscriptionActions.EditNextBillingDate:
          {
            if (subscription.status === SubscriptionStatus.Paused) {
              return /*#__PURE__*/_jsx(FormattedMessage, {
                message: "subscriptions.Actions.edit-next-billing-date.errors.paused"
              });
            }
            if (!isSubcriptionEditableAndActive(subscription.status)) {
              return /*#__PURE__*/_jsx(FormattedMessage, {
                message: actionName === SubscriptionActions.EditNextPaymentDate ? 'subscriptions.Actions.edit-next-payment-date.errors.inactiveSubscription' : 'subscriptions.Actions.edit-next-billing-date.errors.inactiveSubscription'
              });
            }
            if (getIsNextPaymentDateToday(subscription.nextPaymentDueDate)) {
              return /*#__PURE__*/_jsx(FormattedMessage, {
                message: actionName === SubscriptionActions.EditNextPaymentDate ? 'subscriptions.Actions.edit-next-payment-date.errors.paymentProcessing' : 'subscriptions.Actions.edit-next-billing-date.errors.paymentProcessing'
              });
            }
            break;
          }
        case SubscriptionActions.Cancel:
          {
            if (getIsFirstPaymentProcessing(subscription.numberOfCompletedPayments, subscription.paymentStatus)) {
              return /*#__PURE__*/_jsx(FormattedMessage, {
                message: "subscriptions.Actions.cancel.errors.paymentProcessing"
              });
            }
            break;
          }
        case SubscriptionActions.Pause:
          {
            if (!isSubcriptionEditableAndActive(subscription.status) && subscription.status !== SubscriptionStatus.Unpaid) {
              return /*#__PURE__*/_jsx(FormattedMessage, {
                message: "subscriptions.Actions.pause.errors.inactiveSubscription"
              });
            }

            // We allow pause for when a subscription payment is processing
            if (isSubscriptionPaymentProcessing(subscription.paymentStatus)) {
              return null;
            }
            break;
          }
        default:
          return null;
      }
      if (isSubscriptionPaymentProcessing(subscription.paymentStatus) && actionName !== SubscriptionActions.Cancel) {
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: `subscriptions.Actions.${actionName}.errors.paymentProcessing`
        });
      }
      return null;
    }, Object.assign({}, DEFAULT_VALIDATION));
  }, [subscription, isJitaUser, userPermissions.currentUserCanEdit, paymentsEligibility.chargesEnabled, hasBillingWizardGate, hasWriteAccess, isAutomaticPayments]);
}